import PropTypes from 'prop-types';
import React from 'react';
import { Button, Form, Counter, Icon, Numbers, Search, ToastContext, SelectHierarchyLabel } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { fetcher } from '@weezevent/weezjs-core';
import Rate from '../../models/rateModel';
import Group from '../../models/rateGroup';
import RateTemplate from '../../models/rateTemplate';
import GroupsTemplate from '../../models/rateTemplateGroup';
import * as Sentry from '@sentry/browser';
import CardLoader from '../../components/cardLoader';
import { NoItems } from '../../components/no-items';
import PartnerAvailability from '../../models/partnerAvailability';
import StyleRM from '../rates/components/rateManager.module.css';
import Style from './styles/rates-selector.module.css';
import { InheritedProperty } from './../rates/ratesList';
import RatesTable from './../rates/ratesTable';
import { generateClassNameRateName } from '../rates/components/rateManager';
import { RateCategorySeat } from '../rates/components/rateCategorySeat';

function MultipleRatesSelector({ onChange, onCardDone, inventories, partnerId, organizationId, ratesProp, rates, currency }) {
    const toast = React.useContext(ToastContext);
    const { t, i18n } = useTranslation();
    const availability_query = 'inventories=' + inventories.map(x => x.uuid).join(',');
    const [inputSearchValue, setInputSearchValue] = React.useState('');
    const [availability, loading] = PartnerAvailability.useApiModel({
        organizationId: organizationId,
        partnerId,
        query: availability_query,
        onError: () => {
            toast.error(t('ticket.partners.batch.seat.availability_error'));
        }
    });
    const groups_query = 'event_id__in=' + Array.from(new Set(rates.map(r => r.event).filter(e => e))).join(',');
    const [groups, groups_loading] = Group.useApiModel({
        organizationId: organizationId,
        query: groups_query,
        onError: () => {
            toast.error(t('ticket.partners.batch.seat.availability_error'));
        }
    });
    const [groups_template, groups_template_loading] = GroupsTemplate.useApiModel({
        organizationId: organizationId,
        onError: () => {
            toast.error(t('ticket.partners.batch.seat.availability_error'));
        }
    });

    // Admit that we've got only 1 Zone for partner.
    // Lock rates if the availability of the first zone is <=0, None value equal to infinity
    const allLocked = availability?.children?.[0]?.categories?.[0]?.contingents?.[0]?.available ? availability?.children?.[0].categories[0].contingents[0].available <= 0 : false;

    const tableCells = [
        {
            header: t('ticket.common.name'),
            renderRate: (rate, { groups }) => {
                return (
                    <InheritedProperty
                        property="name"
                        object={rate}
                        handleProperty={name => (
                            <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                {name}
                            </div>
                        )}
                        template="rate_template"
                    />
                );
            },
            renderGroup: (group, onToggle) => {
                return (
                    <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                        {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                        <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                        {group.name ? group.name : group.group_template ? groups_template.find(g => g.pk === group.group_template)?.name : '-'}
                    </div>
                );
            }
        },
        {
            header: t('ticket.common.price'),
            renderRate: (rate, { groups }) => {
                return (
                    <InheritedProperty
                        property="price"
                        object={rate}
                        handleProperty={price => (price !== '0.00' ? <Numbers value={price} locale={i18n.language} currency={currency} showNullDecimal /> : t('ticket.rates.free'))}
                        template="rate_template"
                    />
                );
            },
            renderGroup: (group, onToggle) => {
                return null;
            }
        },
        {
            header: t('ticket.common.quantity'),
            renderRate: (rate, args) => {
                return allLocked ? (
                    t('ticket.rates.status.sold_out')
                ) : (
                    <Counter
                        style={{ maxWidth: '150px', marginRight: '15px', minWidth: '75px', width: '150px' }}
                        placeholder={0}
                        value={ratesProp.find(r => r.pk === rate.pk)?.quantity || 0}
                        min={0}
                        onChange={(event, value) => {
                            const ratesObject = { rates: [], rate_templates: [] };
                            rates.forEach(r => {
                                const attr = Number.isInteger(r.pk) ? 'rates' : 'rate_templates';
                                if (rate.pk === r.pk) {
                                    r.quantity = value;
                                }
                                ratesObject[attr].push(r);
                            });
                            onChange({ ...ratesObject }, rate?.category_is_ga);
                        }}
                    />
                );
            },
            renderGroup: (group, onToggle) => {
                return null;
            }
        }
    ];

    const groups_object = React.useMemo(() => {
        const tmp_groups = groups || [];
        const tmp_group_templates = groups_template || [];
        return [...tmp_groups, ...tmp_group_templates].sort((a, b) => a.display_order - b.display_order);
    }, [groups, groups_template]);

    const rates_with_availability = React.useMemo(() => {
        if (!availability || availability.children.length === 0) {
            return [];
        }
        const availability_rates = availability.children?.[0].categories[0].contingents[0].rates.map(r => parseInt(r.id)) || [];
        const filtered_rates = rates.filter(r => {
            if (Number.isInteger(r.pk)) {
                return availability_rates.includes(r.pk);
            }
            // For each inventory
            for (const inventory of inventories) {
                // Check if at least one rate of the rate template belonging to this inventory is in availability
                // So first we find rates from template what has this inventory
                const rates_from_template_with_this_inventory = r.rates.filter(rate => {
                    return rate.inventories.includes(inventory.uuid);
                });
                if (!rates_from_template_with_this_inventory.length) {
                    return false;
                }
                const template_to_keep = rates_from_template_with_this_inventory.some(rate => availability_rates.includes(rate.id));
                return template_to_keep;
            }
            // should not happen cause we always have inventory
            return false;
        });
        return filtered_rates.sort((a, b) => a.display_order - b.display_order);
    }, [rates, availability, inventories]);

    const rates_for_table = React.useMemo(() => {
        if (!inputSearchValue?.trim()) {
            return rates_with_availability;
        }
        const valueToSearch = inputSearchValue.trim().toLowerCase();
        return rates_with_availability.filter(r => r.resolved_name.toLowerCase().includes(valueToSearch));
    }, [rates_with_availability, inputSearchValue]);

    const groups_for_table = React.useMemo(() => {
        // keep groups and parents of groups that contain the rate
        const groups_in_rate = rates_for_table.map(r => r.group);
        const displayed_groups = groups_object
            .filter(group => groups_in_rate.includes(group.id))
            .reduce((acc, group) => {
                acc.push(group.id);

                if (group.parent) {
                    acc.push(group.parent);
                }

                return acc;
            }, []);

        return groups_object.filter(g => {
            g.showRates = true;
            return displayed_groups.includes(g.pk);
        });
    }, [rates_for_table, groups_object]);

    if (loading || groups_loading || groups_template_loading) {
        return <CardLoader />;
    }
    return (
        <>
            <div className={Style['filter']}>
                <Search
                    onChange={(synth, value) => setInputSearchValue(value)}
                    displayResetIcon={true}
                    handleReset={(synth, value) => setInputSearchValue('')}
                    loading={false}
                    placeholder={t('ticket.rates.filters.placeholder_search')}
                />
            </div>
            <RatesTable t={t} rates={rates_for_table} tableCells={tableCells} groups={groups_for_table} selectedGroups={[]} selectedRates={[]} selectable={false} />
        </>
    );
}

function SimpleRateSelector({ onChange, onCardDone, ratesProp, rates }) {
    const { t } = useTranslation();
    const [querySearch, setQuerySearch] = React.useState('');

    const handleQuery = React.useCallback(value => {
        setQuerySearch(value);
    });

    const _onChange = React.useCallback(
        (e, value) => {
            if (!value) return;

            const rates = {
                rates: [],
                rate_templates: []
            };

            const selectedValue = value.value;

            if (Number.isInteger(selectedValue.pk)) {
                rates.rates.push(selectedValue);
            } else {
                rates.rate_templates.push(selectedValue);
            }

            onChange(rates, selectedValue.category_is_ga);
        },
        [onChange]
    );

    function stylizeRateOption(rate) {
        const _categories = [
            {
                uuid: rate.category,
                name: rate.category_name,
                color: rate.category_color
            }
        ];

        return (
            <div className="events-picker-options">
                <div>
                    <RateCategorySeat rate={rate} categories={_categories} />
                </div>
                <div>
                    <div>{`${rate.uuid ? '*' : ''} ${rate.resolved_name}`}</div>
                    {(rate.group_name || rate.category_name) && (
                        <div className="events-picker-options-date">
                            {rate.group_name || ''}
                            {rate.group_name && rate.category_name ? ' • ' : ''}
                            {rate.category_name || ''}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const ratesOptions = React.useMemo(() => {
        if (!rates) return [];

        return rates
            .sort((rate1, rate2) => rate1.group - rate2.group)
            .map(rate => ({
                key: rate.id || rate.pk,
                text: stylizeRateOption(rate),
                name: rate.name || rate.rate_template?.name,
                value: rate,
                category_is_ga: rate.category_is_ga,
                active: true
            }));
    }, [rates]);

    const filteredRates = React.useMemo(() => {
        const searchValue = querySearch?.trim().toLowerCase() ?? '';

        if (!searchValue) {
            return ratesOptions;
        }

        return ratesOptions?.filter(ev => ev.name.toLowerCase().includes(searchValue));
    }, [ratesOptions, querySearch]);

    const handleLabel = React.useCallback(
        value => {
            return ratesOptions.find(o => value?.key === o.key)?.name;
        },
        [ratesOptions]
    );

    if (!rates.length) {
        return <NoItems title={t('ticket.partners.batch.rate.noRates')} />;
    }

    return (
        <>
            <Form.Field>
                <div className={Style['selector-container']}>
                    <SelectHierarchyLabel
                        label={t('ticket.partners.batch.rate.label')}
                        widthIndent="10px"
                        options={ratesOptions}
                        externalOptions={filteredRates}
                        placeholder={t('ticket.common.action.select')}
                        onChange={_onChange}
                        onExternalSearch={handleQuery}
                        searchValue={querySearch}
                        onLabelSelected={handleLabel}
                    />
                </div>
            </Form.Field>
            <Button primary label={t('ticket.common.action.continue')} className={'partner-continue-button'} onClick={onCardDone} disabled={!ratesProp.length} />
        </>
    );
}

export default function PartnerBatchBookingsRates({ onChange, onCardDone, inventories, partnerId, organizationId, ratesProp, isSeated, onSeated, currency }) {
    const [rates, setRates] = React.useState([]);
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [isLoading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!inventories) {
            return null;
        }
        setLoading(true);
        fetcher
            .get(`/ticket/organizations/${organizationId}/partners/${partnerId}/availability/rates`, {
                query: { inventories: inventories.map(x => x.uuid) }
            })
            .then(({ response }) => {
                let rates = response.rates.map(rate => new Rate(rate));
                let ratesTmp = [...rates];
                if (inventories.length > 1) {
                    let rate_templates = response.rate_templates.filter(rt => !rt.removed).map(rate_template => new RateTemplate(rate_template));
                    ratesTmp = [...rates, ...rate_templates];
                }
                onSeated(Boolean(response.is_seated));
                setRates(ratesTmp);
                setLoading(false);
            })
            .catch(e => {
                Sentry.captureException(e);
                setLoading(false);
                toast.error(t('ticket.partners.batch.rate.retrieval_error'));
                window.location.reload();
            });
    }, [inventories]);

    if (isLoading) {
        return <CardLoader />;
    }
    if (isSeated) {
        return <SimpleRateSelector onCardDone={onCardDone} onChange={onChange} ratesProp={ratesProp} rates={rates} />;
    }
    return (
        <MultipleRatesSelector
            inventories={inventories}
            partnerId={partnerId}
            organizationId={organizationId}
            onCardDone={onCardDone}
            onChange={onChange}
            ratesProp={ratesProp}
            rates={rates}
            currency={currency}
        />
    );
}

PartnerBatchBookingsRates.propTypes = {
    onCardDone: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    inventories: PropTypes.array.isRequired,
    ratesProp: PropTypes.array.isRequired,
    partnerId: PropTypes.string.isRequired,
    organizationId: PropTypes.number.isRequired
};
